import ActionIconButton from '../../../components/ui/ActionIconButton';
import { Dialog } from '../../../components/ui';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Icon } from '@iconify/react'
import NoData from '../../../components/common/NoData';
import DefaultProfile from '../../../assets/images/profile/default-profile.png'
import FeedPost from './FeedPost'
import { getMyPosts, toggleLike, addComment, getPostComments, editComment, deleteComment, deletePost } from '../../../services/user/feed.services'
import EditPostModal from './EditPostModal';
import { toast } from '../../../components/ui/notify'
import BitmovinPlayer from '../../../components/common/BitmovinPlayer'
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';


// Move ActionButton outside to prevent recreation on every render
const ActionButton = React.memo(({ icon, count, onClick, isLiked, isLast, buttonStyle, actionButtonStyle }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef(null);
  const isLikeButton = icon === 'mdi:heart-outline' || icon === 'mdi:heart';

  useEffect(() => () => clearTimeout(animationTimeoutRef.current), []);

  const buttonClass = useMemo(() =>
    `btn border ${isLiked ? 'tw-text-rose-600' : 'tw-text-slate-500'}`,
    [isLiked]
  );

  const buttonStyleMemo = useMemo(() =>
    isLast ? { ...actionButtonStyle, marginRight: 0 } : actionButtonStyle,
    [isLast, actionButtonStyle]
  );

  const handleClick = () => {
    if (isLikeButton && !isLiked) {
      clearTimeout(animationTimeoutRef.current);
      setIsAnimating(true);
      animationTimeoutRef.current = setTimeout(() => setIsAnimating(false), 600);
    }
    onClick();
  };

  return (
    <button
      className={`${buttonClass}${isLikeButton ? "   tw-overflow-visible [&_.feed-like-icon_>_svg]:tw-relative [&_.feed-like-icon_>_svg]:tw-z-[1] [&_.feed-like-icon_>_svg]:tw-origin-[center] [&.is-animating_.feed-like-icon::after]:[content:''] [&.is-animating_.feed-like-icon::after]:tw-absolute [&.is-animating_.feed-like-icon::after]:tw-inset-[-7px] [&.is-animating_.feed-like-icon::after]:tw-border-[2px] [&.is-animating_.feed-like-icon::after]:tw-border-solid [&.is-animating_.feed-like-icon::after]:tw-border-[rgba(56,_189,_248,_0.7)] [&.is-animating_.feed-like-icon::after]:tw-rounded-[50%] [&.is-animating_.feed-like-icon::after]:tw-animate-[feed-like-ripple_520ms_ease-out_forwards] [&.is-animating_.feed-like-icon::after]:tw-pointer-events-none [&.is-animating_.feed-like-icon_>_svg]:tw-animate-[feed-like-pop_420ms_cubic-bezier(0.22,_1.35,_0.55,_1)] [&.is-animating_.feed-like-burst]:tw-animate-[feed-like-bubble_520ms_ease-out_forwards] [@media(prefers-reduced-motion:_reduce)]:[&.is-animating_.feed-like-icon_>_svg]:tw-animate-[none] [@media(prefers-reduced-motion:_reduce)]:[&.is-animating_.feed-like-burst]:tw-animate-[none] [@media(prefers-reduced-motion:_reduce)]:[&.is-animating_.feed-like-icon::after]:tw-animate-[none] feed-like-button" : ""}${isAnimating ? "   is-animating" : ""}`}
      onClick={handleClick}
      style={buttonStyleMemo}
    >
      <div className="tw-flex tw-items-center tw-justify-center">
        <span className={isLikeButton ? "tw-relative tw-inline-flex tw-items-center tw-justify-center feed-like-icon" : undefined}>
          <Icon icon={icon} style={{fontSize: '1.2rem'}} />
          {isLikeButton && [1, 2, 3, 4, 5].map((bubble) => <span key={bubble} className={"tw-absolute tw-inset-[50%] tw-w-[5px] tw-h-[8px] tw-rounded-[70%_30%_70%_30%] [background:linear-gradient(145deg,_#7dd3fc,_#0284c7)] tw-shadow-[0_0_3px_rgba(14,_165,_233,_0.5)] tw-opacity-[0] tw-pointer-events-none [&:nth-child(2)]:[--x:-15px] [&:nth-child(2)]:[--y:-18px] [&:nth-child(3)]:[--x:1px] [&:nth-child(3)]:[--y:-24px] [&:nth-child(3)]:![animation-delay:35ms] [&:nth-child(4)]:[--x:17px] [&:nth-child(4)]:[--y:-13px] [&:nth-child(4)]:![animation-delay:70ms] [&:nth-child(5)]:[--x:20px] [&:nth-child(5)]:[--y:7px] [&:nth-child(5)]:![animation-delay:45ms] [&:nth-child(6)]:[--x:-18px] [&:nth-child(6)]:[--y:9px] [&:nth-child(6)]:![animation-delay:90ms] feed-like-burst"} />)}
        </span>
        {count && <span className="tw-ms-1" style={{fontSize: '0.9rem'}}>{count}</span>}
      </div>
    </button>
  );
});

const MyFeed = () => {
  const { timeZone } = useTimezone();
  const formatFeedDate = (value) => value === 'Just now' ? value : formatUtcInTimeZone(value, timeZone, 'MMMM D, YYYY');
  // Posts state
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [postingNewPost, setPostingNewPost] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Comments state
  const [newComment, setNewComment] = useState({});
  const [showComments, setShowComments] = useState({});
  const [postComments, setPostComments] = useState({}); // Store comments for each post
  const [commentsLoading, setCommentsLoading] = useState({}); // Loading state for comments
  const [editingComment, setEditingComment] = useState({}); // Track which comment is being edited
  const [editCommentText, setEditCommentText] = useState({}); // Store edit text for each comment
  const [showFullText, setShowFullText] = useState({}); // Track which posts show full text
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Track delete confirmation modal
  const [postToDelete, setPostToDelete] = useState(null); // Store post ID to delete
  const [deletingPost, setDeletingPost] = useState(false); // Track delete operation state
  const [postToEdit, setPostToEdit] = useState(null);

  const user_id = JSON.parse(localStorage.getItem('user')).id;

  // Load my posts
  const loadMyPosts = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await getMyPosts(page, 5);

      if (response.success) {
        const transformedPosts = response.data.posts.map(post => ({
          id: post.id,
          user: {
            name: post.user_name,
            avatar: post.user_avatar || DefaultProfile
          },
          content: post.description,
          media: post.media_url ? {
            type: post.media_type,
            url: post.media_url
          } : null,
          isLiked: post.is_liked_by_user === 1,
          likes: post.likes_count,
          commentsCount: post.comments_count,
          created_at: post.created_at
        }));

        if (append) {
          setPosts(prev => [...prev, ...transformedPosts]);
        } else {
          setPosts(transformedPosts);
          // Store user profile from first post
          if (transformedPosts.length > 0) {
            setUserProfile({
              name: transformedPosts[0].user.name,
              profile_pic_url: transformedPosts[0].user.avatar
            });
          }
        }

        setCurrentPage(page);
        setHasMore(response.data.pagination.has_more);
      } else {
      }
    } catch (error) {
      toast.error('Error loading posts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load initial posts
  useEffect(() => {
    loadMyPosts(1);
  }, [loadMyPosts]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Check if user has scrolled to bottom (with 100px threshold)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (!loadingMore && hasMore) {
          loadMorePosts();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore]);

  // Button styles
  // Memoized styles to prevent re-renders
  const buttonStyle = useMemo(() => ({
    backgroundColor: 'transparent',
    borderColor: '#dee2e6'
  }), []);

  const actionButtonStyle = useMemo(() => ({
    flex: 1,
    marginRight: '10px',
    ...buttonStyle
  }), [buttonStyle]);

  // Load comments for a specific post
  const loadPostComments = useCallback(async (postId) => {
    try {
      setCommentsLoading(prev => ({ ...prev, [postId]: true }));

      // Load all comments at once by setting a high page size
      const response = await getPostComments(postId, 1, 1000);

      if (response.success) {
        const newComments = response.data.comments.map(comment => ({
          id: comment.id,
          user: comment.user_name,
          user_id: comment.user_id,
          avatar: comment.user_avatar || DefaultProfile,
          text: comment.comment,
          timestamp: comment.commented_at
        }));

        setPostComments(prev => ({
          ...prev,
          [postId]: newComments
        }));
      } else {
        toast.error('Failed to load comments');
      }
    } catch (error) {
      toast.error('Error loading comments');
    } finally {
      setCommentsLoading(prev => ({ ...prev, [postId]: false }));
    }
  }, []);

  // Event handlers
  const handleLike = async (postId) => {
    try {
      const response = await toggleLike(postId);
      if (response.success) {
        setPosts(posts.map(post =>
          post.id === postId
            ? {
                ...post,
                isLiked: !post.isLiked,
                likes: post.isLiked ? post.likes - 1 : post.likes + 1
              }
            : post
        ));
      } else {
        toast.error('Failed to update like');
      }
    } catch (error) {
      toast.error('Error toggling like');
    }
  };

  const handleComment = (postId) => {
    const isOpening = !showComments[postId];
    setShowComments(prev => ({ ...prev, [postId]: !prev[postId] }));

    // Load comments when opening comments section for the first time
    if (isOpening && !postComments[postId]) {
      loadPostComments(postId);
    }
  };

  const handleSubmitComment = async (postId) => {
    const commentText = newComment[postId];
    if (!commentText || !commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (commentText.length > 400) {
      toast.error('Comment cannot exceed 400 characters');
      return;
    }

    try {
      const response = await addComment(postId, commentText.trim());
      if (response.success) {
        // Update the post's comments count
        setPosts(posts.map(post =>
          post.id === postId
            ? { ...post, commentsCount: post.commentsCount + 1 }
            : post
        ));

        // Add the new comment to the comments list
        const newCommentObj = {
          id: response.data.comment.id,
          user: response.data.comment.user_name,
          user_id: response.data.comment.user_id,
          avatar: response.data.comment.user_avatar || DefaultProfile,
          text: response.data.comment.comment,
          timestamp: 'Just now'
        };

        setPostComments(prev => ({
          ...prev,
          [postId]: [newCommentObj, ...(prev[postId] || [])]
        }));

        setNewComment(prev => ({ ...prev, [postId]: '' }));
        toast.success('Comment added successfully');
      } else {
        toast.error('Failed to add comment');
      }
    } catch (error) {
      toast.error('Error adding comment');
    }
  };

  const handlePostSubmit = async (newPost) => {
    setPostingNewPost(true);

    // Simulate API delay and then refresh the feed
    setTimeout(async () => {
      try {
        await loadMyPosts(1); // Reload posts to include the new one
        toast.success('Post created successfully');
      } finally {
        setPostingNewPost(false);
      }
    }, 2000); // 2 second delay
  };

  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadMyPosts(currentPage + 1, true);
    }
  }, [loadMyPosts, loadingMore, hasMore, currentPage]);

  // Render functions
  const renderMedia = (media) => {
    if (!media) return null;

    if (media.type === 'image') {
      return (
        <div className="tw-relative" style={{ width: '100%', maxHeight: '400px', overflow: 'hidden', borderRadius: '8px' }}>
          <img
            src={media.url}
            className="tw-max-w-full"
            alt="Post media"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '400px',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>
      );
    } else if (media.type === 'video') {
      return (
        <div className="tw-relative tw-rounded-xl tw-overflow-hidden" style={{ width: '100%', background: '#000' }}>
          <BitmovinPlayer
            src={media.url}
            title="Post video"
            objectFit="contain"
            style={{ maxHeight: '400px', width: '100%' }}
          />
        </div>
      );
    }
    return null;
  };

  const renderPostContent = (content, post) => {
    if (!content) return null;

    const hasMedia = post.media && (post.media.type === 'image' || post.media.type === 'video');
    const postTextStyle = { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6' };

    // For text-only posts, show full content
    if (!hasMedia) {
      return (
        <div>
          <p className="tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] tw-text-[rgb(71_85_105_/_var(--tw-text-opacity,_1))] ui-card-text tw-mb-2" style={postTextStyle}>{content}</p>
        </div>
      );
    }

    // For posts with media, show truncated text with "Show more" option
    const shouldTruncate = content.length > 100;
    const isShowingFull = showFullText[post.id];
    const displayText = isShowingFull ? content : content.substring(0, 100) + (shouldTruncate ? '...' : '');

    return (
      <div>
        <p className="tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] tw-text-[rgb(71_85_105_/_var(--tw-text-opacity,_1))] ui-card-text tw-mb-2" style={postTextStyle}>
          {displayText}
          {shouldTruncate && (
            <button
              className={"!tw-text-[var(--primary-color)] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-m-0 [transition:opacity_0.2s_ease] tw-bg-[transparent] [--tw-text-opacity:1] [&:hover]:tw-opacity-[0.8] [&:hover]:tw-text-[#dc3545] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] ui-button ui-button-link tw-p-0 tw-ms-2 tw-text-brand-600 tw-no-underline"}
              onClick={() => setShowFullText(prev => ({ ...prev, [post.id]: !isShowingFull }))}
            >
              {isShowingFull ? 'Show less' : 'Show more'}
            </button>
          )}
        </p>
      </div>
    );
  };

  // Memoized comment handlers to prevent re-renders
  const handleCommentChange = useCallback((postId, value) => {
    setNewComment(prev => ({ ...prev, [postId]: value }));
  }, []);

  const handleCommentKeyDown = useCallback((e, postId) => {
    if (e.key === 'Enter') {
      handleSubmitComment(postId);
    }
  }, [handleSubmitComment]);

  const handleCommentSubmitClick = useCallback((postId) => {
    handleSubmitComment(postId);
  }, [handleSubmitComment]);

  const handleEditComment = async (postId, commentId) => {
    const editText = editCommentText[commentId];
    if (!editText || !editText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (editText.length > 400) {
      toast.error('Comment cannot exceed 400 characters');
      return;
    }

    try {
      const response = await editComment(commentId, editText.trim());
      if (response.success) {
        // Update the comment in the comments list
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].map(comment =>
            comment.id === commentId
              ? { ...comment, text: editText.trim() }
              : comment
          )
        }));

        setEditingComment(prev => ({ ...prev, [commentId]: false }));
        setEditCommentText(prev => ({ ...prev, [commentId]: '' }));
        toast.success('Comment updated successfully');
      } else {
        toast.error('Failed to update comment');
      }
    } catch (error) {
      toast.error('Error updating comment');
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      const response = await deleteComment(commentId);
      if (response.success) {
        // Update the post's comments count
        setPosts(posts.map(post =>
          post.id === postId
            ? { ...post, commentsCount: post.commentsCount - 1 }
            : post
        ));

        // Remove the comment from the comments list
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].filter(comment => comment.id !== commentId)
        }));

        toast.success('Comment deleted successfully');
      } else {
        toast.error('Failed to delete comment');
      }
    } catch (error) {
      toast.error('Error deleting comment');
    }
  };

  const handleEditCommentChange = useCallback((commentId, value) => {
    setEditCommentText(prev => ({ ...prev, [commentId]: value }));
  }, []);

  const handleEditCommentKeyDown = useCallback((e, postId, commentId) => {
    if (e.key === 'Enter') {
      handleEditComment(postId, commentId);
    }
  }, [handleEditComment]);

  // Delete post handlers
  const handleDeletePostClick = useCallback((postId) => {
    setPostToDelete(postId);
    setShowDeleteModal(true);
  }, []);

  const handleDeletePostConfirm = async () => {
    if (!postToDelete) return;

    setDeletingPost(true);
    try {
      const response = await deletePost(postToDelete);
      if (response.success) {
        // Remove the post from the posts array
        setPosts(prevPosts => prevPosts.filter(post => post.id !== postToDelete));
        toast.success('Post deleted successfully!', { position: "top-center" });
      } else {
        toast.error(response.message || response.error?.message || 'Failed to delete post');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeletingPost(false);
      setShowDeleteModal(false);
      setPostToDelete(null);
    }
  };

  const handleDeletePostCancel = () => {
    setShowDeleteModal(false);
    setPostToDelete(null);
  };

  const handlePostSaved = (updatedPost) => {
    const post = updatedPost?.post || updatedPost;
    setPosts((currentPosts) => currentPosts.map((currentPost) => {
      if (currentPost.id !== post?.id) return currentPost;
      if (!post?.user_name) return { ...currentPost, ...post };

      return {
        ...currentPost,
        id: post.id,
        user: { name: post.user_name, avatar: post.user_avatar || currentPost.user.avatar || DefaultProfile },
        content: post.description,
        media: post.media_url ? { type: post.media_type, url: post.media_url } : null,
        isLiked: post.is_liked_by_user == null ? currentPost.isLiked : post.is_liked_by_user === 1,
        likes: post.likes_count == null ? currentPost.likes : post.likes_count,
        commentsCount: post.comments_count == null ? currentPost.commentsCount : post.comments_count,
        created_at: post.created_at || currentPost.created_at,
      };
    }));
  };

  const renderComments = (post) => {
    if (!showComments[post.id]) return null;

    const comments = postComments[post.id] || [];
    const isLoading = commentsLoading[post.id];

    return (
      <div className="tw-border-t tw-pt-4 tw-mt-4">
        <h6 className="tw-mb-4">Comments ({post.commentsCount || 0})</h6>

        {/* Comment Input */}
        <div className="tw-flex tw-mb-4">
          <img src={userProfile?.profile_pic_url || DefaultProfile} className="tw-mt-1 tw-size-9 tw-shrink-0 tw-rounded-full tw-object-cover" alt="Profile" />
          <div className="tw-min-w-0 tw-flex-1">
            <input
              type="text"
              className="tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-border-brand-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-100"
              placeholder="Write a comment..."
              value={newComment[post.id] || ''}
              onChange={(e) => handleCommentChange(post.id, e.target.value)}
              onKeyDown={(e) => handleCommentKeyDown(e, post.id)}
              maxLength={400}
            />
            <div className="tw-flex tw-justify-end tw-mt-1">
              <small className={(newComment[post.id] || "").length > 360 ? "!tw-text-[var(--warning-color)] tw-text-amber-600" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"}>
                {(newComment[post.id] || '').length}/400 characters
              </small>
            </div>
          </div>
          <button
            className="tw-ml-2 tw-inline-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-brand-600 tw-p-0 tw-text-white tw-shadow-sm tw-transition-colors hover:tw-bg-brand-700 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-brand-600 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-100 disabled:tw-text-slate-400 disabled:tw-shadow-none"
            type="button"
            aria-label="Send comment"
            onClick={() => handleCommentSubmitClick(post.id)}
            disabled={!newComment[post.id] || !newComment[post.id].trim()}
          >
            <Icon icon="mdi:send" width={20} height={20} />
          </button>
        </div>

        {/* Comments Loading State */}
        {isLoading ? (
          <div className="tw-text-center tw-py-4">
            <div className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm" role="status">
              <span className="tw-sr-only">Loading comments...</span>
            </div>
            <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-mt-2 tw-text-slate-500 tw-text-sm">Loading comments...</p>
          </div>
        ) : (
          <>
            {/* Comments Container */}
            <div id={`comments-container-${post.id}`}>
              {/* Existing Comments */}
              {comments.map(comment => (
                <div key={comment.id} className="tw-flex tw-mb-2">
                  <img src={comment.avatar} className="tw-rounded-full tw-me-2" alt={comment.user} style={{width: '32px', height: '32px'}} />
                  <div className="tw-bg-slate-50 tw-rounded-xl tw-p-2 tw-grow">
                    <div className="tw-flex tw-justify-between tw-items-start">
                      <div className="tw-font-bold">{comment.user}</div>
                      {/* Show edit/delete options only for user's own comments */}
                      {comment.user_id === user_id && (
                        <div className="tw-flex tw-gap-1">
                          {editingComment[comment.id] ? (
                            <>
                              <button
                                className="tw-inline-flex tw-size-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-p-0 tw-transition-colors focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 tw-border-emerald-200 tw-bg-white tw-text-emerald-700 hover:tw-bg-emerald-50 focus-visible:tw-outline-emerald-600"
                                type="button"
                                aria-label="Save comment edit"
                                onClick={() => handleEditComment(post.id, comment.id)}
                              >
                                <Icon icon="mdi:check" width={16} height={16} />
                              </button>
                              <button
                                className="tw-inline-flex tw-size-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-p-0 tw-transition-colors focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-100 focus-visible:tw-outline-slate-500"
                                type="button"
                                aria-label="Cancel comment edit"
                                onClick={() => {
                                  setEditingComment(prev => ({ ...prev, [comment.id]: false }));
                                  setEditCommentText(prev => ({ ...prev, [comment.id]: '' }));
                                }}
                              >
                                <Icon icon="mdi:close" width={16} height={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <ActionIconButton
                                icon="tabler:pencil"
                                label="Edit comment"
                                tone="edit"
                                type="button"
                                onClick={() => {
                                  setEditingComment(prev => ({ ...prev, [comment.id]: true }));
                                  setEditCommentText(prev => ({ ...prev, [comment.id]: comment.text }));
                                }}
                                title="Edit comment"
                              />
                              <ActionIconButton
                                icon="tabler:trash"
                                label="Delete comment"
                                tone="danger"
                                type="button"
                                onClick={() => handleDeleteComment(post.id, comment.id)}
                                title="Delete comment"
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {editingComment[comment.id] ? (
                      <div className="tw-mt-2">
                        <input
                          type="text"
                          className={"tw-text-[0.75rem] tw-leading-[1rem] [&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input ui-input-sm"}
                          value={editCommentText[comment.id] || ''}
                          onChange={(e) => handleEditCommentChange(comment.id, e.target.value)}
                          onKeyDown={(e) => handleEditCommentKeyDown(e, post.id, comment.id)}
                          maxLength={400}
                          autoFocus
                        />
                        <div className="tw-flex tw-justify-end tw-mt-1">
                          <small className={(editCommentText[comment.id] || "").length > 360 ? "!tw-text-[var(--warning-color)] tw-text-amber-600" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"}>
                            {(editCommentText[comment.id] || '').length}/400 characters
                          </small>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        wordWrap: 'break-word',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%'
                      }}>
                        {comment.text}
                      </div>
                    )}

                    <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-mt-1">{formatFeedDate(comment.timestamp)}</div>
                  </div>
                </div>
              ))}


            </div>
          </>
        )}
      </div>
    );
  };



  return (
    <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container tw-py-6">
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-justify-center"}>
        <div className="tw-min-w-[0px] ui-column md:tw-w-2/3 tw-p-0">
                    {/* Feed Info */}
                    <div className="tw-text-center tw-mb-6">
            <div className="tw-p-4 tw-bg-slate-50 tw-rounded-xl">
              <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-mb-0 tw-text-sm">
                This is your personal feed where you can see all your posts and updates.
                Create new posts below and manage your content here.
              </p>
            </div>
          </div>

          {/* Profile Header */}
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
            <div></div>
            <div className="tw-flex tw-items-center">
              <div className="tw-text-right tw-me-4">
                <h5 className="tw-mb-0">My Posts</h5>
                <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Your personal posts and updates</small>
              </div>
              <img
                src={userProfile?.profile_pic_url || DefaultProfile}
                className="tw-rounded-full"
                alt={userProfile?.name || "Profile"}
                style={{width: '50px', height: '50px'}}
              />
            </div>
          </div>

          {/* Create Post Component */}
          <FeedPost onPostSubmit={handlePostSubmit} userProfile={userProfile} />


          {/* New Post Loading State */}
          {postingNewPost && (
            <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-mb-6"} style={{
              animation: 'fadeIn 0.5s ease-in-out',
              border: '2px dashed #007bff',
              backgroundColor: '#f8f9fa'
            }}>
              <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body tw-text-center tw-py-6">
                <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600 tw-mb-4" role="status">
                  <span className="tw-sr-only">Creating post...</span>
                </div>
                <h6 className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-mb-2">Creating your post...</h6>
                <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Please wait while we process your content</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="tw-text-center tw-py-6">
              <div className="tw-w-[1.5em] tw-h-[1.5em] tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner" role="status">
                <span className="tw-sr-only">Loading...</span>
              </div>
              <p className="!tw-text-[var(--secondary-color)] tw-mt-2 tw-text-slate-500">Loading your posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <NoData
              message="No Posts Yet"
              description="Be the first to share something!"
              icon="mdi:post-outline"
            />
          ) : (
            <>
              {/* Posts Feed */}
              {posts.map((post) => (
              <div key={post.id} className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-mb-6"}>
                <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                  {/* Post Header */}
                  <div className="tw-flex tw-items-center tw-mb-4">
                    <img src={post.user.avatar} className="tw-rounded-full tw-me-4" alt={post.user.name} style={{width: '40px', height: '40px'}} />
                    <div className="tw-grow">
                      <h6 className="tw-mb-0">{post.user.name}</h6>
                      <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">{formatFeedDate(post.created_at)}</small>
                    </div>
                    <div className="tw-ml-2 tw-flex tw-shrink-0 tw-gap-2">
                      <ActionIconButton
                        icon="tabler:pencil"
                        label="Edit post"
                        tone="edit"
                        type="button"
                        onClick={() => setPostToEdit(post)}
                        title="Edit post"
                      />
                      <ActionIconButton
                        icon="tabler:trash"
                        label="Delete post"
                        tone="danger"
                        type="button"
                        onClick={() => handleDeletePostClick(post.id)}
                        title="Delete post"
                      />
                    </div>
                  </div>

                  {/* Post Content */}
                  <div className="tw-mb-4">
                    {renderPostContent(post.content, post)}
                    {renderMedia(post.media)}
                  </div>

                  {/* Action Buttons */}
                  <div className="tw-flex tw-justify-between">
                    <ActionButton
                      icon={post.isLiked ? "mdi:heart" : "mdi:heart-outline"}
                      count={post.likes}
                      onClick={() => handleLike(post.id)}
                      isLiked={post.isLiked}
                      buttonStyle={buttonStyle}
                      actionButtonStyle={actionButtonStyle}
                    />
                    <ActionButton
                      icon="mdi:comment-outline"
                      count={post.commentsCount || 0}
                      onClick={() => handleComment(post.id)}
                      isLast={true}
                      buttonStyle={buttonStyle}
                      actionButtonStyle={actionButtonStyle}
                    />
                  </div>

                  {/* Comments Section */}
                  {renderComments(post)}
                </div>
              </div>
              ))}

              {/* Loading More Posts Indicator */}
              {loadingMore && (
                <div className="tw-text-center tw-py-6">
                  <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600 tw-mb-2" role="status">
                    <span className="tw-sr-only">Loading more posts...</span>
                  </div>
                  <p className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-mb-0">Loading more posts...</p>
                </div>
              )}

              {!hasMore && posts.length > 0 && (
                <div className="tw-text-center tw-py-4">
                  <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500">You've reached the end of your posts!</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Dialog show onHide={handleDeletePostCancel} keyboard={!(deletingPost)} backdrop={(deletingPost) ? 'static' : true}>
              <Dialog.Header closeButton={!(deletingPost)}>
                <Dialog.Title >
                  <Icon icon="mdi:delete" className="!tw-text-[var(--danger-color)] tw-me-2 tw-text-rose-600" />
                  Delete Post
                </Dialog.Title>

              </Dialog.Header>
              <Dialog.Body >
                <div className="tw-flex tw-items-center tw-mb-4">
                  <Icon icon="mdi:alert-circle" className="!tw-text-[var(--warning-color)] tw-text-amber-600 tw-me-2" style={{ fontSize: '1.5rem' }} />
                  <span>Are you sure you want to delete this post?</span>
                </div>
                <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">
                  This action cannot be undone. The post and all its comments will be permanently deleted.
                </p>
              </Dialog.Body>
              <Dialog.Footer >
                <button
                  type="button"
                  className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-secondary"}
                  onClick={handleDeletePostCancel}
                  disabled={deletingPost}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] ui-button ui-button-danger"}
                  onClick={handleDeletePostConfirm}
                  disabled={deletingPost}
                >
                  {deletingPost ? (
                    <>
                      <div className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status">
                        <span className="tw-sr-only">Deleting...</span>
                      </div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:delete" className="tw-me-2" />
                      Delete Post
                    </>
                  )}
                </button>
              </Dialog.Footer>
            </Dialog>
      )}
      <EditPostModal
        post={postToEdit}
        onClose={() => setPostToEdit(null)}
        onSaved={handlePostSaved}
      />
    </div>
  );
};

export default MyFeed;
